import React, { useEffect, useState } from "react";
import { Specification } from "../../types";
import specificationImg from "../../assets/images/specification.png";

interface Props {
  className?: string;
  specification: Specification;

  // UI controls
  showValues?: boolean; // show "current / max"
  showPercentLabel?: boolean; // show numeric percent near the bar (not shown by default)
  showIcon?: boolean; // toggle the right-hand image
  barHeight?: number; // progress bar height in px
  animate?: boolean; // animate bar change
  animationDurationMs?: number; // duration for width animation

  // Colors (accepts CSS strings)
  lowColor?: string; // used when percent < lowThreshold
  highColor?: string; // used when percent >= 100
  midGradient?: string; // used for normal gradient

  // thresholds
  lowThreshold?: number; // below this percent use lowColor

  // Events
  onClick?: (spec: Specification) => void;
  onHover?: (spec: Specification) => void;

  // Accessibility
  ariaLabel?: string;
  disabled?: boolean;
}

const SpecificationCard = ({
  className = "",
  specification,
  showValues = true,
  showPercentLabel = false,
  showIcon = true,
  barHeight = 8,
  animate = true,
  animationDurationMs = 500,
  lowColor = "linear-gradient(90deg, red, #FF6B6B)",
  highColor = "#47FF94",
  midGradient = "linear-gradient(90deg, #47F9FF, #47FF94)",
  lowThreshold = 20,
  onClick,
  onHover,
  ariaLabel,
  disabled = false,
}: Props) => {
  // percent clamped between 0 and 100
  const percent = Math.min(
    100,
    Math.max(0, (specification.currentValue / specification.maxValue) * 100)
  );

  // local state for animated width (keeps layout stable when animate=true)
  const [animWidth, setAnimWidth] = useState(animate ? 0 : percent);

  useEffect(() => {
    if (!animate) {
      setAnimWidth(percent);
      return;
    }

    // animate to the new percent
    // simple approach: set the target width after a tick so CSS transition applies
    const id = window.setTimeout(() => setAnimWidth(percent), 20);
    return () => window.clearTimeout(id);
  }, [percent, animate]);

  // determine background for the filled portion
  const filledBackground =
    percent < lowThreshold
      ? lowColor
      : percent >= 100
      ? highColor
      : midGradient;

  return (
    <article
      role="group"
      aria-label={ariaLabel ?? `Specification ${specification.name}`}
      onClick={() => !disabled && onClick?.(specification)}
      onMouseEnter={() => !disabled && onHover?.(specification)}
      className={`flex items-center space-x-3 justify-between text-white ${className} ${
        disabled ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      {/* Main info: name, (optional) values, and progress bar */}
      <main className="flex-1 text-left">
        {/* Name and optional numeric values */}
        <div className="flex items-center justify-between text-[14px] 1366px:text-[15px] 3440px:text-[32px]">
          <p className="truncate">{specification?.name}</p>

          {showValues && (
            <p className="whitespace-nowrap">
              {specification.currentValue} / {specification.maxValue}
            </p>
          )}
        </div>

        {/* Progress bar container */}
        <div
          className="rounded-sm mt-2 overflow-hidden"
          style={{
            padding: 2,
            minHeight: Math.max(6, barHeight),
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
            borderRadius: 6,
            border: "0.5px solid rgba(255,255,255,0.08)",
          }}
        >
          <div
            style={{
              height: Math.max(4, barHeight - 4),
              width: "100%",
              background: "rgba(255,255,255,0.06)",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              // filled portion
              style={{
                width: `${animWidth}%`,
                height: "100%",
                borderRadius: 4,
                transition: animate
                  ? `width ${animationDurationMs}ms ease-out`
                  : "none",
                background: filledBackground,
              }}
              className="rounded-sm"
            />
          </div>
        </div>

        {/* optional percent label */}
        {showPercentLabel && (
          <div className="mt-1 text-[12px] text-white/60">
            {Math.round(percent)}%
          </div>
        )}
      </main>

      {/* decorative icon on the right (optional) */}
      {showIcon && (
        <div
          className="w-[44px] h-[44px] 1366px:w-[60px] 1366px:h-[60px] 3440px:w-[90px] 3440px:h-[90px] relative"
          aria-hidden
        >
          <img
            src={specificationImg}
            alt=""
            className="absolute top-[-60%] left-[-60%] min-w-[200%] min-h-[200%] object-contain"
            draggable={false}
          />
        </div>
      )}
    </article>
  );
};

export default SpecificationCard;
